import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useParams } from 'react-router-dom'
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  Paper,
  Stack,
  Tabs,
  Tab,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Tooltip,
} from '@mui/material'
import DesktopWindowsIcon from '@mui/icons-material/DesktopWindows'
import TabletIcon from '@mui/icons-material/Tablet'
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone'
import RefreshIcon from '@mui/icons-material/Refresh'
import TuneIcon from '@mui/icons-material/Tune'
import ViewSidebarIcon from '@mui/icons-material/ViewSidebar'
import PaletteIcon from '@mui/icons-material/Palette'
import CodeIcon from '@mui/icons-material/Code'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import { fetchTheme, publishTheme, updateTheme } from '../../actions/themeActions'
import { ThemeApi } from '../../api/themeApi'
import SectionList from './SectionList'
import SectionSettings from './SectionSettings'
import ThemeSettingsPanel from './ThemeSettingsPanel'
import ThemeCustomCssPanel from './ThemeCustomCssPanel'
import useTranslation from '../../hooks/useTranslation'

const DEVICE_WIDTHS = {
  desktop: '100%',
  tablet: '768px',
  mobile: '390px',
}

const SAVE_DEBOUNCE_MS = 700

const saveStatusLabel = (status, t) => {
  switch (status) {
    case 'pending':
      return t('labels.themes.saveStatus.pending')
    case 'saving':
      return t('labels.themes.saveStatus.saving')
    case 'saved':
      return t('labels.themes.saveStatus.saved')
    case 'error':
      return t('labels.themes.saveStatus.error')
    default:
      return t('labels.themes.saveStatus.ready')
  }
}

const saveStatusColor = (status) => {
  switch (status) {
    case 'saved':
      return 'success'
    case 'error':
      return 'error'
    case 'saving':
    case 'pending':
      return 'warning'
    default:
      return 'default'
  }
}

export default function ThemeEditor() {
  const { t } = useTranslation()
  const { id } = useParams()
  const dispatch = useDispatch()
  const history = useHistory()
  const iframeRef = useRef(null)

  const { activeTheme, loading, updating, error } = useSelector((state) => state.themes)

  const [draftTheme, setDraftTheme] = useState(null)
  const [selectedSectionId, setSelectedSectionId] = useState(null)
  const [device, setDevice] = useState('desktop')
  const [previewToken, setPreviewToken] = useState(null)
  const [previewRevision, setPreviewRevision] = useState(Date.now())
  const [publishConfirm, setPublishConfirm] = useState(false)
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const [saveState, setSaveState] = useState('idle')
  const [settingsTab, setSettingsTab] = useState('section')

  useEffect(() => {
    dispatch(fetchTheme(id))
  }, [dispatch, id])

  useEffect(() => {
    if (!activeTheme?.id) return

    ThemeApi.getPreviewToken(activeTheme.id)
      .then((res) => {
        setPreviewToken(res.data.previewToken)
      })
      .catch((err) => {
        console.error('Failed to get preview token:', err)
      })
  }, [activeTheme?.id])

  useEffect(() => {
    if (!activeTheme) return

    setDraftTheme((prev) => {
      if (!prev || prev.id !== activeTheme.id) {
        return {
          ...activeTheme,
          settings: activeTheme.settings || {},
          sections: activeTheme.sections || [],
        }
      }

      if (!hasUnsavedChanges && prev.updated_at !== activeTheme.updated_at) {
        return {
          ...activeTheme,
          settings: activeTheme.settings || {},
          sections: activeTheme.sections || [],
        }
      }

      return prev
    })
  }, [activeTheme, hasUnsavedChanges])

  useEffect(() => {
    if (!draftTheme?.sections?.length) {
      setSelectedSectionId(null)
      return
    }

    const sectionStillExists = draftTheme.sections.some(
      (section) => section.id === selectedSectionId
    )

    if (!sectionStillExists) {
      setSelectedSectionId(draftTheme.sections[0].id)
      setSettingsTab('section')
    }
  }, [draftTheme?.sections, selectedSectionId])

  useEffect(() => {
    if (!draftTheme) return
    if (!selectedSectionId && settingsTab === 'section' && !draftTheme.sections?.length) {
      setSettingsTab('theme')
    }
  }, [draftTheme, selectedSectionId, settingsTab])

  const selectedSection = useMemo(
    () => draftTheme?.sections?.find((section) => section.id === selectedSectionId) || null,
    [draftTheme?.sections, selectedSectionId]
  )

  const draftSignature = useMemo(() => {
    if (!draftTheme) return ''
    return JSON.stringify({
      name: draftTheme.name,
      settings: draftTheme.settings,
      sections: draftTheme.sections,
    })
  }, [draftTheme])

  const persistTheme = async (themeToSave) => {
    if (!themeToSave?.id) return

    setSaveState('saving')

    try {
      await dispatch(updateTheme(themeToSave.id, {
        name: themeToSave.name,
        settings: themeToSave.settings,
        sections: themeToSave.sections,
      }))

      setHasUnsavedChanges(false)
      setSaveState('saved')
      setPreviewRevision(Date.now())
    } catch (err) {
      setSaveState('error')
    }
  }

  useEffect(() => {
    if (!draftTheme || !hasUnsavedChanges) return

    setSaveState('pending')

    const timeout = setTimeout(() => {
      persistTheme(draftTheme)
    }, SAVE_DEBOUNCE_MS)

    return () => clearTimeout(timeout)
  }, [draftSignature, hasUnsavedChanges])

  const applyDraftChange = (updater) => {
    setDraftTheme((prev) => {
      const next =
        typeof updater === 'function'
          ? updater(prev)
          : updater

      return next
    })

    setHasUnsavedChanges(true)
  }

  const handleSectionsChange = (newSections) => {
    applyDraftChange((prev) => ({
      ...prev,
      sections: newSections,
    }))
  }

  const handleSectionSettingsChange = (sectionId, newSettings) => {
    applyDraftChange((prev) => ({
      ...prev,
      sections: prev.sections.map((section) =>
        section.id === sectionId
          ? { ...section, settings: newSettings }
          : section
      ),
    }))
  }

  const handleSectionMetaChange = (sectionId, patch) => {
    applyDraftChange((prev) => ({
      ...prev,
      sections: prev.sections.map((section) =>
        section.id === sectionId
          ? { ...section, ...patch }
          : section
      ),
    }))
  }

  const handleThemeSettingsChange = (newSettings) => {
    applyDraftChange((prev) => ({
      ...prev,
      settings: newSettings,
    }))
  }

  const handleThemeNameChange = (newName) => {
    applyDraftChange((prev) => ({
      ...prev,
      name: newName,
    }))
  }

  const refreshPreview = () => {
    setPreviewRevision(Date.now())
  }

  const handlePublish = async () => {
    try {
      if (hasUnsavedChanges && draftTheme) {
        await persistTheme(draftTheme)
      }

      await dispatch(publishTheme(draftTheme.id))
      setPublishConfirm(false)
      history.push('/admin/themes')
    } catch (err) {
      alert(t('labels.themes.failedToPublish'))
    }
  }

  const getPreviewUrl = () => {
    if (!draftTheme || !previewToken) return ''

    const store = JSON.parse(localStorage.getItem('store') || '{}')
    const baseUrl = store.primary_domain
      ? `http://${store.primary_domain}`
      : `${process.env.REACT_APP_STORE_URL}/${store.store_url}`

    return `${baseUrl}/preview?themeId=${draftTheme.id}&previewToken=${previewToken}&rev=${previewRevision}`
  }

  if (loading || !draftTheme) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>{t('labels.themes.loadingTheme')}</Typography>
      </Box>
    )
  }

  if (draftTheme.status !== 'draft') {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>{t('labels.themes.onlyDraftsCanBeEdited')}</Typography>
        <Button onClick={() => history.push('/admin/themes')} sx={{ mt: 2 }}>
          {t('labels.themes.backToStorefrontText')}
        </Button>
      </Box>
    )
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: '#f8fafc', mt: -3 }}>
      <Paper
        sx={{
          p: 1.5,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderRadius: 0,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <Tooltip title={t('labels.themes.backToStorefront')}>
            <IconButton
              onClick={() => history.push('/admin/themes')}
              size="small"
              sx={{
                border: 1,
                borderColor: 'divider',
                borderRadius: 2,
              }}
            >
              <ArrowBackIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Box>
            <Typography variant="h6">{draftTheme.name}</Typography>
            <Typography variant="caption" color="text.secondary">
              {t('labels.themes.draftThemeEditor')}
            </Typography>
          </Box>

          <Chip
            size="small"
            label={saveStatusLabel(saveState, t)}
            color={saveStatusColor(saveState)}
            variant="outlined"
          />

          {error && (
            <Typography variant="caption" color="error">
              {error}
            </Typography>
          )}
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center">
          <ToggleButtonGroup
            value={device}
            exclusive
            onChange={(e, value) => value && setDevice(value)}
            size="small"
          >
            <ToggleButton value="desktop">
              <DesktopWindowsIcon fontSize="small" />
            </ToggleButton>
            <ToggleButton value="tablet">
              <TabletIcon fontSize="small" />
            </ToggleButton>
            <ToggleButton value="mobile">
              <PhoneIphoneIcon fontSize="small" />
            </ToggleButton>
          </ToggleButtonGroup>

          <Tooltip title={t('labels.themes.refreshPreview')}>
            <IconButton onClick={refreshPreview}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>

          <Tooltip
            title={
              updating
                ? t('labels.themes.saveStatus.saving')
                : !hasUnsavedChanges
                  ? t('labels.themes.noUnsavedChanges')
                  : ''
            }
          >
            <span>
              <Button
                variant="outlined"
                onClick={() => persistTheme(draftTheme)}
                disabled={!hasUnsavedChanges || updating}
              >
                {t('labels.themes.saveNow')}
              </Button>
            </span>
          </Tooltip>

          <Button
            variant="outlined"
            component="a"
            href={getPreviewUrl()}
            target="_blank"
            rel="noreferrer"
            disabled={!previewToken}
          >
            {t('labels.themes.openFullPreview')}
          </Button>

          <Button
            variant="contained"
            color="success"
            onClick={() => setPublishConfirm(true)}
          >
            {t('labels.themes.publish')}
          </Button>
        </Stack>
      </Paper>

      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        <Paper
          sx={{
            width: 300,
            overflow: 'auto',
            borderRadius: 0,
            borderRight: 1,
            borderColor: 'divider',
            bgcolor: '#fff',
          }}
        >
          <SectionList
            sections={draftTheme.sections || []}
            selectedId={selectedSectionId}
            onSelect={(sectionId) => {
              setSelectedSectionId(sectionId)
              setSettingsTab('section')
              if (sectionId && iframeRef.current?.contentWindow) {
                let targetOrigin = ''
                try {
                  targetOrigin = new URL(getPreviewUrl()).origin
                } catch (_) {
                  return
                }
                iframeRef.current.contentWindow.postMessage(
                  { type: 'scrollToSection', sectionId },
                  targetOrigin
                )
              }
            }}
            onChange={handleSectionsChange}
          />
        </Paper>

        <Box
          sx={{
            flex: 1,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'stretch',
            bgcolor: '#f1f5f9',
            p: 2,
            overflow: 'auto',
          }}
        >
          <Box sx={{ width: DEVICE_WIDTHS[device], maxWidth: '100%', height: '100%' }}>
            {previewToken ? (
              <iframe
                ref={iframeRef}
                src={getPreviewUrl()}
                title="Theme Preview"
                style={{
                  width: '100%',
                  height: '100%',
                  border: '1px solid #dbe4ee',
                  borderRadius: '12px',
                  background: '#fff',
                }}
              />
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                }}
              >
                <Typography color="text.secondary">{t('labels.themes.loadingPreview')}</Typography>
              </Box>
            )}
          </Box>
        </Box>

        <Paper
          sx={{
            width: 380,
            overflow: 'auto',
            borderRadius: 0,
            borderLeft: 1,
            borderColor: 'divider',
            bgcolor: '#fff',
          }}
        >
          <Box sx={{ p: 2 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
              <ViewSidebarIcon fontSize="small" />
              <Typography variant="subtitle1">{t('labels.themes.editor')}</Typography>
            </Stack>

            <Tabs
              value={settingsTab}
              onChange={(e, value) => setSettingsTab(value)}
              sx={{ mb: 2 }}
            >
              <Tab
                value="section"
                label={t('labels.themes.tabs.section')}
                icon={<TuneIcon fontSize="small" />}
                iconPosition="start"
                disabled={!selectedSection}
              />
              <Tab
                value="theme"
                label={t('labels.themes.tabs.theme')}
                icon={<PaletteIcon fontSize="small" />}
                iconPosition="start"
              />
              <Tab
                value="customCss"
                label={t('labels.themes.tabs.customCss')}
                icon={<CodeIcon fontSize="small" />}
                iconPosition="start"
              />
            </Tabs>

            {settingsTab === 'section' && selectedSection ? (
              <SectionSettings
                section={selectedSection}
                themeId={draftTheme.id}
                onChange={(newSettings) =>
                  handleSectionSettingsChange(selectedSection.id, newSettings)
                }
                onSectionMetaChange={(patch) =>
                  handleSectionMetaChange(selectedSection.id, patch)
                }
                allSections={draftTheme.sections || []}
                onRefreshPreview={refreshPreview}
              />
            ) : settingsTab === 'customCss' ? (
              <ThemeCustomCssPanel
                settings={draftTheme.settings}
                sections={draftTheme.sections || []}
                onSettingsChange={handleThemeSettingsChange}
              />
            ) : (
              <ThemeSettingsPanel
                themeId={draftTheme.id}
                themeName={draftTheme.name}
                settings={draftTheme.settings}
                onThemeNameChange={handleThemeNameChange}
                onSettingsChange={handleThemeSettingsChange}
              />
            )}
          </Box>
        </Paper>
      </Box>

      <Dialog open={publishConfirm} onClose={() => setPublishConfirm(false)}>
        <DialogTitle>{t('labels.themes.publishTheme')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('labels.themes.publishDialogDescription', { name: draftTheme.name })}
          </DialogContentText>

          {hasUnsavedChanges && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              {t('labels.themes.unsavedChangesWarning')}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPublishConfirm(false)}>{t('labels.themes.cancel')}</Button>
          <Button onClick={handlePublish} variant="contained" color="success">
            {t('labels.themes.publish')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}